import React, { useEffect, useState } from "react";
import { CATEGORIES, BLOG_URL } from "../data/categories";
import { fetchLatestPosts } from "../api/posts";

const POST_COUNT = 6;

function BlogLink({ to, className, children }) {
  return (
    <a
      href={to}
      className={className}
      target="_blank"
      rel="noopener noreferrer"
    >
      {children}
    </a>
  );
}

function PostCard({ post }) {
  // Featured images are AVIF. Safari below 16.4 can't decode them, so treat a
  // load failure the same as a missing image rather than showing a broken icon.
  const [imageFailed, setImageFailed] = useState(false);

  return (
    <article className="post-card">
      <BlogLink to={post.url} className="post-thumbnail-link">
        {post.image && !imageFailed ? (
          <img
            className="post-thumbnail"
            src={post.image}
            srcSet={post.imageSrcSet ?? undefined}
            sizes="(max-width: 700px) 100vw, (max-width: 1200px) 50vw, 380px"
            alt={post.imageAlt}
            loading="lazy"
            onError={() => setImageFailed(true)}
          />
        ) : (
          <div className="post-thumbnail post-thumbnail-fallback">✂️</div>
        )}
      </BlogLink>

      <div className="post-content">
        <h3 className="post-title">
          <BlogLink to={post.url}>{post.title}</BlogLink>
        </h3>

        <div className="post-excerpt">{post.excerpt}</div>

        <div className="post-meta">
          <span>📅 {post.date}</span>
        </div>

        <BlogLink to={post.url} className="read-more">
          Read on the blog &rarr;
        </BlogLink>
      </div>
    </article>
  );
}

function PostSkeleton() {
  return (
    <article className="post-card post-card-skeleton" aria-hidden="true">
      <div className="post-thumbnail skeleton-block"></div>
      <div className="post-content">
        <div className="skeleton-line skeleton-line-title"></div>
        <div className="skeleton-line"></div>
        <div className="skeleton-line"></div>
        <div className="skeleton-line skeleton-line-short"></div>
      </div>
    </article>
  );
}

function Home() {
  const [posts, setPosts] = useState([]);
  const [status, setStatus] = useState("loading");

  useEffect(() => {
    const controller = new AbortController();

    fetchLatestPosts(POST_COUNT, controller.signal)
      .then((latest) => {
        setPosts(latest);
        setStatus("ready");
      })
      .catch((error) => {
        if (error.name === "AbortError") return;
        setStatus("error");
      });

    return () => controller.abort();
  }, []);

  return (
    <>
      {/* Hero Section */}
      <section className="hero-section">
        <div className="hero-content">
          <h1>Your Bachelor's Guidebook</h1>
          <p>
            Straight talk on health, habits, and the things nobody sat you down
            and explained.
            <br />
            Everything you need to thrive on your own.
          </p>
          <a href="#latest-posts" className="hero-button">
            Start Reading
          </a>
        </div>
      </section>

      {/* Category Cards - every one of these hands off to the blog */}
      <section className="categories-section">
        <div className="content-wrapper">
          <h2 className="section-title">Your Guide To Better Living</h2>
          <p className="section-subtitle">
            Find exactly what you need to improve your bachelor lifestyle
          </p>

          <div className="category-grid">
            {CATEGORIES.map((category) => (
              <div className="category-card" key={category.slug}>
                <span className="category-icon">{category.icon}</span>
                <h3>{category.name}</h3>
                <p>{category.description}</p>
                <BlogLink to={category.url} className="category-button">
                  {category.cta}
                </BlogLink>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Latest Posts - pulled live from the blog on every visit */}
      <section className="latest-posts-section" id="latest-posts">
        <div className="content-wrapper">
          <h2 className="section-title">Latest Articles</h2>
          <p className="section-subtitle">
            Fresh insights and practical tips for better bachelor living
          </p>

          {status === "loading" && (
            <div className="posts-grid">
              {Array.from({ length: POST_COUNT }, (_, i) => (
                <PostSkeleton key={i} />
              ))}
            </div>
          )}

          {status === "error" && (
            <div className="posts-empty">
              <p>
                We couldn't load the latest articles just now. They're all
                waiting for you on the blog.
              </p>
            </div>
          )}

          {status === "ready" && posts.length > 0 && (
            <div className="posts-grid">
              {posts.map((post) => (
                <PostCard key={post.id} post={post} />
              ))}
            </div>
          )}

          {status === "ready" && posts.length === 0 && (
            <div className="posts-empty">
              <p>No articles published yet. Check back soon.</p>
            </div>
          )}

          <div className="latest-posts-footer">
            <BlogLink to={BLOG_URL} className="hero-button">
              Visit the Blog
            </BlogLink>
          </div>
        </div>
      </section>
    </>
  );
}

export default Home;
